import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { communicationService } from '../../services/communicationService';
import { Megaphone } from 'lucide-react';

const AnnouncementsPage = () => {
  const [announcements, setAnnouncements] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);
      // Get only published announcements
      const res = await communicationService.getAnnouncements({ 
        status: 'published',
        isPublished: true 
      });
      const data = res.data?.data || res.data || [];
      setAnnouncements(Array.isArray(data) ? data : data.announcements || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load announcements');
      console.error('Error fetching announcements:', e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchAnnouncements();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchAnnouncements}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Announcements</h2>
        <Button variant="outline" size="sm" onClick={fetchAnnouncements}>Refresh</Button>
      </div>

      {(!announcements || announcements.length === 0) ? (
        <EmptyState 
          icon={Megaphone}
          title="No announcements"
          description="School announcements will appear here."
          action
          actionLabel="Refresh"
          onAction={fetchAnnouncements}
        />
      ) : (
        <div className="space-y-4">
          {Array.isArray(announcements) ? announcements.map((a) => (
            <Card key={a.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {a.title || a.subject}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                        <span>{new Date(a.createdAt || a.date).toLocaleDateString()}</span>
                        {a.type && (
                          <>
                            <span>•</span>
                            <span className="capitalize">{a.type}</span>
                          </>
                        )}
                        {a.priority && (
                          <>
                            <span>•</span>
                            <span className="capitalize">{a.priority} priority</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {a.content || a.message ? (
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {a.content || a.message}
                    </p>
                  ) : null}
                  {a.createdBy && (
                    <p className="text-xs text-gray-500">
                      By: {a.createdBy?.firstName || ''} {a.createdBy?.lastName || ''}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )) : null}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsPage;


